import { useCallback, useEffect } from "react";
import { BuyerUser, CreateBuyerUser, UpdateBuyerUser } from "../types/models/BuyerUser.d";
import { UpdateEntityBuyerUser } from "../types/models/BuyerUser";
import { useGetBuyerUserListQuery, useInsertBuyerUserMutation, useUpdateBuyerUserMutation, useDeleteBuyerUserMutation, } from "../apis/BuyerUser.api";
import { useDeleteDialog, useDescriptionAlert } from '../../share';
import { useNavigate } from "react-router";
import { t } from "@lingui/core/macro";

export default function useBuyerUser(page: number, pageSize: number) {

    const navigate = useNavigate()
    const { openDialog, closeDialog } = useDeleteDialog();

    const { openSuccessAlert, openFailAlert } = useDescriptionAlert()

    const [insertBuyerUserMutation, { 
        isLoading: insertBuyerUserLoading, 
        isSuccess: insertBuyerUserIsSuccess,
        isError: insertBuyerUserIsError,
        error: insertBuyerUserError,
    }] = useInsertBuyerUserMutation()
    const [updateBuyerUserMutation, { 
        isLoading: updateBuyerUserLoading,
        isSuccess: updateBuyerUserIsSuccess,
        isError: updateBuyerUserIsError,
        error: updateBuyerUserError,
    }] = useUpdateBuyerUserMutation()
    const [deleteBuyerUserMutation, { 
        isLoading: deleteBuyerUserLoading,
        isSuccess: deleteBuyerUserIsSuccess,
        isError: deleteBuyerUserIsError,
        error: deleteBuyerUserError,
    }] = useDeleteBuyerUserMutation()

    const loading =
        insertBuyerUserLoading ||
        updateBuyerUserLoading ||
        deleteBuyerUserLoading ||
        false

    const insertBuyerUser = useCallback(async (buyerUser: CreateBuyerUser, redirectPath: string|undefined) => {
        try {
            const data = await insertBuyerUserMutation(buyerUser).unwrap()
            if (data) {
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate(`../${data.id}`, {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("add error", e)
            openFailAlert(t`Add error: ` + e.toString())
        }
    }, [insertBuyerUserMutation, navigate])
    
    useEffect(() => {
        if (insertBuyerUserIsSuccess) {
            openSuccessAlert('Buyer User created')
        } else if (insertBuyerUserIsError) {
            console.log("add error", insertBuyerUserError)
            openFailAlert(t`Buyer User add error: ` + insertBuyerUserError?.data?.message)
        }
    }, [
        insertBuyerUserIsSuccess,
        insertBuyerUserIsError,
        insertBuyerUserError
    ]);

    const updateBuyerUser = useCallback(async (buyerUser: UpdateEntityBuyerUser, redirectPath: string|undefined) => {
        try {
            const data = await updateBuyerUserMutation(buyerUser).unwrap();
            if (data != null) {
                //if update success
                if (redirectPath) {
                    if (redirectPath !== '.') {
                        navigate(redirectPath, {relative: 'path'})
                    }
                } else {
                    navigate('..', {relative: 'path'})
                }
                return data;
            }
        } catch (e) {
            console.log("update error", e)
            openFailAlert(t`Update error: ` + e.toString())
        }
    }, [updateBuyerUserMutation, navigate])
    
    useEffect(() => {
        if (updateBuyerUserIsSuccess) {
            openSuccessAlert(t`Buyer User updated`)
        } else if (updateBuyerUserIsError) {
            console.log("update error", updateBuyerUserError)
            openFailAlert(t`Buyer User update error: ` + updateBuyerUserError?.data?.message)
        }
    }, [
        updateBuyerUserIsSuccess,
        updateBuyerUserIsError,
        updateBuyerUserError
    ]);

    const deleteBuyerUser = useCallback((buyerUser: BuyerUser, redirectPath: string|undefined) => {
        openDialog(async () => {
            buyerUser = {
                ...buyerUser,
            }
            try {
                const { data } = await deleteBuyerUserMutation(buyerUser);
                if (data != null) {
                    if (redirectPath) {
                        navigate(redirectPath, {relative: 'path'})
                    }
                }
            } catch (e) {
                console.log("delete error", e)
                openFailAlert(t`Delete error: ` + e.toString())
            }
            closeDialog()
        });
    }, [deleteBuyerUserMutation, closeDialog, openDialog])
    
    useEffect(() => {
        if (deleteBuyerUserIsSuccess) {
            openSuccessAlert(t`Buyer User deleted`)
        } else if (deleteBuyerUserIsError) {
            console.log("delete error", deleteBuyerUserError)
            openFailAlert(t`Buyer User delete error: ` + deleteBuyerUserError?.data?.message)
        }
    }, [
        deleteBuyerUserIsSuccess,
        deleteBuyerUserIsError,
        deleteBuyerUserError
    ]);

    return {
        loading,
        insertBuyerUser,
        updateBuyerUser,
        deleteBuyerUser
    }
}
